import React from 'react';
import styled from 'styled-components';
import { FiEdit, FiMessageCircle, FiKey, FiPhone, FiCalendar } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import theme from '../../styles/theme';
import { formatPhone } from '../../utils/formatters';

const CardContainer = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.primary[300]};
  }
`;

const Header = styled.div`
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[600]} 100%);
  padding: ${theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${theme.colors.text.primary};
`;

const Avatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${theme.borderRadius.full};
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  text-transform: uppercase;
  border: 3px solid rgba(255, 255, 255, 0.3);
`;

const InfoBlock = styled.div`
  flex: 1;
  margin-left: ${theme.spacing[4]};
`;

const Title = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[1]} 0;
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: ${theme.fontSizes.sm};
  margin: 0;
`;

const ContentSection = styled.div`
  padding: ${theme.spacing[4]};
`;

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[4]};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  word-break: break-all;
`;

const DetailIcon = styled.div`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  min-width: 20px;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
`;

const ActionButton = styled.button`
  flex: 1;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${props => props.$primary ? theme.colors.primary[500] : theme.colors.border.primary};
  background: ${props => props.$primary ? theme.colors.primary[500] : 'transparent'};
  color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.background.primary};
    color: ${props => props.$primary ? theme.colors.text.primary : theme.colors.primary[500]};
    border-color: ${props => props.$primary ? theme.colors.primary[600] : theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR');
};

const getInitials = (ambiente) => {
  if (!ambiente) return 'W';
  return ambiente.substring(0, 2).toUpperCase();
};


const ParametrosWhatsappCard = ({ parametro }) => {
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate(`/parametros-whatsapp/${parametro.id}/editar`);
  };

  return (
    <CardContainer>
      <Header>
        <Avatar>
          {getInitials(parametro.ambiente)}
        </Avatar>
        <InfoBlock>
          <Title>{parametro.ambiente}</Title>
          <Subtitle>{parametro.nome || 'Sem Nome'}</Subtitle>
        </InfoBlock>
      </Header>

      <ContentSection>
        <Details>
          <DetailItem>
            <DetailIcon><FiPhone /></DetailIcon>
            <span>{parametro.contato ? formatPhone(parametro.contato) : 'Sem Contato Informado'}</span>
          </DetailItem>
          <DetailItem>
            <DetailIcon><FiKey /></DetailIcon>
            <span>Token: {parametro.token ? parametro.token.substring(0, 10) + '...' : 'Não configurado'}</span>
          </DetailItem>
          <DetailItem>
            <DetailIcon><FiCalendar /></DetailIcon>
            <span>Atualizado em {formatDate(parametro.updated_at)}</span>
          </DetailItem>
        </Details>

        <Actions>
          <ActionButton $primary onClick={handleEdit}>
            <ActionIcon><FiEdit /></ActionIcon>
            Editar
          </ActionButton>
        </Actions>
      </ContentSection>
    </CardContainer>
  );
};

export default ParametrosWhatsappCard;
